import { ReactElement, SyntheticEvent, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { Alignment, Fit, Layout, useRive } from '@rive-app/react-canvas';

import { fetchEventPatch, fetchEventStatusPlayers } from '../../services/event.service';
import { IOrganisationEventItem, IOrganisationEventsResponse, IOrganisationEventRiveAsset } from '../../types/@organisation';

const GRASS_BG = 'https://mintfinity-storage.s3.eu-west-2.amazonaws.com/site_images/grass.jpg';
const DEFAULT_STATE_MACHINE = 'State Machine 1';
const TEAM_DEFAULT_STORAGE_KEY = 'xdos.graphics.team-defaults';

type TOrientation = 'portrait' | 'landscape';
type TPairAction = '' | 'preview-portrait' | 'preview-landscape' | 'apply-to-event' | 'set-team-default';

type TPlayer = {
  ownerId: string;
  name: string;
  statement: string | null;
};

type TPairAsset = IOrganisationEventRiveAsset | null;

type TGraphicsPair = {
  signature: string;
  identifier: string;
  portrait: TPairAsset;
  landscape: TPairAsset;
  updatedAt: number;
  eventIds: string[];
  eventNames: string[];
  teamIds: string[];
  teamNames: string[];
  usageCount: number;
};

type TPreviewState = {
  open: boolean;
  event: IOrganisationEventItem | null;
  orientation: TOrientation;
  pair: TGraphicsPair | null;
};

interface IProps {
  organisationName: string;
  events: IOrganisationEventsResponse;
}

const sanitizeOrganisationPrefix = (name: string): string => {
  const compact = String(name || 'ORG')
    .replace(/[^a-z0-9]+/gi, '')
    .toUpperCase();

  return compact || 'ORG';
};

const toSequenceLetters = (index: number): string => {
  let value = index + 1;
  let letters = '';

  while (value > 0) {
    const remainder = (value - 1) % 26;
    letters = String.fromCharCode(65 + remainder) + letters;
    value = Math.floor((value - 1) / 26);
  }

  return letters;
};

const getAssetSignature = (asset: TPairAsset): string => {
  if (!asset) return 'none';

  return String(asset.originalname || asset.key || asset.location || 'none')
    .trim()
    .toLowerCase();
};

const getPairSignature = (portrait: TPairAsset, landscape: TPairAsset): string => {
  return `${getAssetSignature(portrait)}::${getAssetSignature(landscape)}`;
};

const getEventServiceId = (event: any): string => {
  return String(event?.eventId || event?._id || event?.id || event?.qdosEventId || '').trim();
};

const formatUpdatedAt = (value: number): string => {
  if (!value) return 'Unknown update';

  try {
    return new Date(value).toLocaleString();
  } catch {
    return 'Unknown update';
  }
};

const loadTeamDefaults = (): Record<string, string> => {
  try {
    const raw = window.localStorage.getItem(TEAM_DEFAULT_STORAGE_KEY);

    if (!raw) return {};

    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

const saveTeamDefaults = (value: Record<string, string>): void => {
  try {
    window.localStorage.setItem(TEAM_DEFAULT_STORAGE_KEY, JSON.stringify(value));
  } catch {
    // ignore storage errors in preview tooling
  }
};

const GraphicsRivePreview = ({
  src,
  subject,
  context,
  narrative,
  orientation,
}: {
  src: string;
  subject: string;
  context: string;
  narrative: string;
  orientation: TOrientation;
}): ReactElement => {
  const { rive, RiveComponent } = useRive({
    src,
    stateMachines: DEFAULT_STATE_MACHINE,
    autoplay: true,
    layout: new Layout({
      fit: Fit.Contain,
      alignment: Alignment.BottomCenter,
    }),
  });

  useEffect(() => {
    if (!rive) return;

    const runtime = rive as any;

    if (typeof runtime.setTextRunValue === 'function') {
      runtime.setTextRunValue('_subject', subject || 'Name');
      runtime.setTextRunValue('_context', context || 'Event');
      runtime.setTextRunValue('_narrative', narrative || 'Statement');
    }

    if (typeof runtime.play === 'function') {
      runtime.play(DEFAULT_STATE_MACHINE);
    }
  }, [context, narrative, rive, subject]);

  return (
    <Box
      sx={{
        width: '100%',
        aspectRatio: orientation === 'portrait' ? '9 / 16' : '16 / 9',
        borderRadius: '18px',
        overflow: 'hidden',
        border: '1px solid #dce4ec',
        backgroundImage: `url(${GRASS_BG})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        position: 'relative',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: orientation === 'portrait'
            ? 'linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0) 35%, rgba(0,0,0,0.10) 100%)'
            : 'linear-gradient(180deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0) 25%, rgba(0,0,0,0.12) 100%)',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          bottom: 0,
          height: '25%',
          width: '100%',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'center',
        }}
      >
        <RiveComponent style={{ width: '100%', height: '100%' }} />
      </Box>
    </Box>
  );
};

const OrganisationGraphicsPanel = ({ organisationName, events }: IProps): ReactElement => {
  const [eventState, setEventState] = useState<IOrganisationEventsResponse>(events);
  const [selectedTeamId, setSelectedTeamId] = useState<string>(events.teams[0]?.teamId || '');
  const [targetEventId, setTargetEventId] = useState<string>(events.teams[0]?.events[0]?.eventId || '');
  const [preview, setPreview] = useState<TPreviewState>({
    open: false,
    event: null,
    orientation: 'portrait',
    pair: null,
  });
  const [playersByEventId, setPlayersByEventId] = useState<Record<string, TPlayer[]>>({});
  const [loadingPlayers, setLoadingPlayers] = useState<boolean>(false);
  const [selectedOwnerId, setSelectedOwnerId] = useState<string>('');
  const [teamDefaults, setTeamDefaults] = useState<Record<string, string>>(() => loadTeamDefaults());
  const [pairActions, setPairActions] = useState<Record<string, TPairAction>>({});
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [savingEventId, setSavingEventId] = useState<string>('');

  useEffect(() => {
    setEventState(events);
  }, [events]);

  const pairLibrary = useMemo(() => {
    const pairMap = new Map<string, Omit<TGraphicsPair, 'identifier'>>();

    for (const team of eventState.teams) {
      for (const event of team.events) {
        if (!event.rivePortrait || !event.riveLandscape) continue;

        const signature = getPairSignature(event.rivePortrait, event.riveLandscape);
        const updatedAt = Date.parse(String((event as any)?.updatedAt || '')) || Number(event.eventStartTime || 0) || 0;
        const existing = pairMap.get(signature);

        if (!existing) {
          pairMap.set(signature, {
            signature,
            portrait: event.rivePortrait,
            landscape: event.riveLandscape,
            updatedAt,
            eventIds: [getEventServiceId(event)],
            eventNames: [event.name],
            teamIds: [team.teamId],
            teamNames: [team.teamName],
            usageCount: 1,
          });
          continue;
        }

        existing.updatedAt = Math.max(existing.updatedAt, updatedAt);
        existing.usageCount += 1;

        const eventId = getEventServiceId(event);
        if (eventId && !existing.eventIds.includes(eventId)) {
          existing.eventIds.push(eventId);
        }
        if (event.name && !existing.eventNames.includes(event.name)) {
          existing.eventNames.push(event.name);
        }
        if (team.teamId && !existing.teamIds.includes(team.teamId)) {
          existing.teamIds.push(team.teamId);
        }
        if (team.teamName && !existing.teamNames.includes(team.teamName)) {
          existing.teamNames.push(team.teamName);
        }
      }
    }

    const sorted = Array.from(pairMap.values()).sort((a, b) => b.updatedAt - a.updatedAt);
    const prefix = sanitizeOrganisationPrefix(organisationName);

    return sorted.map((pair, index) => ({
      ...pair,
      identifier: `${prefix}-${toSequenceLetters(index)}`,
    }));
  }, [eventState.teams, organisationName]);

  const selectedTeam = useMemo(() => {
    return eventState.teams.find((team) => team.teamId === selectedTeamId) || eventState.teams[0] || null;
  }, [eventState.teams, selectedTeamId]);

  const selectedTargetEvent = useMemo(() => {
    return selectedTeam?.events.find((event) => getEventServiceId(event) === targetEventId) || selectedTeam?.events[0] || null;
  }, [selectedTeam, targetEventId]);

  useEffect(() => {
    if (!selectedTeam) return;

    if (!selectedTeam.events.find((event) => getEventServiceId(event) === targetEventId)) {
      setTargetEventId(getEventServiceId(selectedTeam.events[0]));
    }
  }, [selectedTeam, targetEventId]);

  const getPairByEvent = (event: IOrganisationEventItem): TGraphicsPair | null => {
    if (!event.rivePortrait || !event.riveLandscape) return null;

    const signature = getPairSignature(event.rivePortrait, event.riveLandscape);
    return pairLibrary.find((pair) => pair.signature === signature) || null;
  };

  const getTeamDefaultPair = (): TGraphicsPair | null => {
    if (!selectedTeam?.teamId) return null;
    const signature = teamDefaults[selectedTeam.teamId];
    if (!signature) return null;
    return pairLibrary.find((pair) => pair.signature === signature) || null;
  };

  const loadPlayers = async (event: IOrganisationEventItem): Promise<TPlayer[]> => {
    const eventId = getEventServiceId(event);
    if (!eventId) return [];

    if (playersByEventId[eventId]) {
      return playersByEventId[eventId];
    }

    setLoadingPlayers(true);

    try {
      const response = await fetchEventStatusPlayers(eventId);
      const players = Array.isArray(response?.data?.players) ? response.data.players : [];
      setPlayersByEventId((prev) => ({ ...prev, [eventId]: players }));
      return players;
    } catch (error: any) {
      setMessage({ type: 'error', text: error?.response?.data?.message || error?.message || 'Unable to load QDOS player statements for preview.' });
      return [];
    } finally {
      setLoadingPlayers(false);
    }
  };

  const openPreview = async (
    event: IOrganisationEventItem,
    orientation: TOrientation,
    pairOverride?: TGraphicsPair | null,
  ): Promise<void> => {
    const players = await loadPlayers(event);
    setSelectedOwnerId(players[0]?.ownerId || '');
    setPreview({
      open: true,
      event,
      orientation,
      pair: pairOverride || getPairByEvent(event),
    });
  };

  const closePreview = (): void => {
    setPreview({ open: false, event: null, orientation: 'portrait', pair: null });
    setSelectedOwnerId('');
  };

  const applyPairToEvent = async (event: IOrganisationEventItem, pair: TGraphicsPair): Promise<void> => {
    const eventId = getEventServiceId(event);

    if (!eventId) {
      setMessage({ type: 'error', text: 'This event does not have a valid service identifier.' });
      return;
    }

    setSavingEventId(eventId);
    setMessage(null);

    try {
      await fetchEventPatch(eventId, {
        rivePortrait: pair.portrait,
        riveLandscape: pair.landscape,
      });

      setEventState((prev) => ({
        ...prev,
        teams: prev.teams.map((team) => ({
          ...team,
          events: team.events.map((item) => {
            if (getEventServiceId(item) !== eventId) return item;
            return {
              ...item,
              rivePortrait: pair.portrait,
              riveLandscape: pair.landscape,
            };
          }),
        })),
      }));

      setMessage({
        type: 'success',
        text: `Graphic pair ${pair.identifier} applied to ${event.name}.`,
      });
    } catch (error: any) {
      setMessage({ type: 'error', text: error?.response?.data?.message || error?.message || 'Unable to apply the graphic pair to this event.' });
    } finally {
      setSavingEventId('');
    }
  };

  const handleSetTeamDefault = (teamId: string, pair: TGraphicsPair): void => {
    const next = {
      ...teamDefaults,
      [teamId]: pair.signature,
    };

    setTeamDefaults(next);
    saveTeamDefaults(next);
    setMessage({ type: 'success', text: `Team default set to graphic pair ${pair.identifier}.` });
  };

  const handlePairAction = async (pair: TGraphicsPair, action: TPairAction): Promise<void> => {
    setPairActions((prev) => ({ ...prev, [pair.signature]: action }));

    if (!action) return;

    if (action === 'set-team-default') {
      if (!selectedTeam?.teamId) return;
      handleSetTeamDefault(selectedTeam.teamId, pair);
      setPairActions((prev) => ({ ...prev, [pair.signature]: '' }));
      return;
    }

    if (!selectedTargetEvent) {
      setMessage({ type: 'info', text: 'Select a target event before previewing or applying a graphic pair.' });
      return;
    }

    if (action === 'preview-portrait') {
      await openPreview(selectedTargetEvent, 'portrait', pair);
    }

    if (action === 'preview-landscape') {
      await openPreview(selectedTargetEvent, 'landscape', pair);
    }

    if (action === 'apply-to-event') {
      await applyPairToEvent(selectedTargetEvent, pair);
    }

    setPairActions((prev) => ({ ...prev, [pair.signature]: '' }));
  };

  const previewPlayers = useMemo(() => {
    if (!preview.event) return [];
    const eventId = getEventServiceId(preview.event);
    return playersByEventId[eventId] || [];
  }, [playersByEventId, preview.event]);

  const selectedPlayer = useMemo(() => {
    return previewPlayers.find((player) => player.ownerId === selectedOwnerId) || previewPlayers[0] || null;
  }, [previewPlayers, selectedOwnerId]);

  const previewAsset = useMemo(() => {
    if (!preview.pair) return null;
    return preview.orientation === 'portrait' ? preview.pair.portrait : preview.pair.landscape;
  }, [preview.orientation, preview.pair]);

  const teamDefaultPair = getTeamDefaultPair();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography sx={{ fontSize: '24px', fontWeight: 700, color: '#17324d' }}>Graphics</Typography>
        <Chip label={`Teams: ${eventState.summary.teamCount}`} />
        <Chip label={`Pairs: ${pairLibrary.length}`} />
        <Chip label="Text map: _subject / _context / _narrative" variant="outlined" />
      </Box>

      {message ? <Alert severity={message.type}>{message.text}</Alert> : null}

      {!eventState.teams.length ? (
        <Alert severity="info">No organisation events are available for graphics management yet.</Alert>
      ) : (
        <>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', lg: '340px 1fr' },
              gap: 3,
              alignItems: 'start',
            }}
          >
            <Paper elevation={0} variant="outlined" sx={{ borderRadius: '16px', overflow: 'hidden' }}>
              <Box sx={{ px: 2, py: 2, background: '#fafbfd' }}>
                <Typography sx={{ fontSize: '15px', fontWeight: 700, color: '#17324d' }}>Teams</Typography>
                <Typography sx={{ fontSize: '13px', color: '#6b7c8f', mt: 0.5 }}>
                  Select a team to manage its event graphics and default pair.
                </Typography>
              </Box>
              <Divider />
              <Tabs
                value={selectedTeam?.teamId || false}
                orientation="vertical"
                variant="scrollable"
                onChange={(_event: SyntheticEvent, value: string) => setSelectedTeamId(value)}
                sx={{ minHeight: '420px' }}
              >
                {eventState.teams.map((team) => (
                  <Tab
                    key={team.teamId}
                    value={team.teamId}
                    label={
                      <Stack alignItems="flex-start" spacing={0.5} sx={{ py: 0.5 }}>
                        <Typography sx={{ fontSize: '14px', fontWeight: 700, textTransform: 'none' }}>
                          {team.teamName}
                        </Typography>
                        <Typography sx={{ fontSize: '12px', color: '#6b7c8f', textTransform: 'none' }}>
                          {team.events.length} events
                        </Typography>
                      </Stack>
                    }
                    sx={{ alignItems: 'flex-start', textTransform: 'none', px: 2 }}
                  />
                ))}
              </Tabs>
            </Paper>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {selectedTeam ? (
                <>
                  <Paper elevation={0} variant="outlined" sx={{ p: 2.5, borderRadius: '16px' }}>
                    <Stack spacing={1.25}>
                      <Typography sx={{ fontSize: '22px', fontWeight: 700, color: '#17324d' }}>
                        {selectedTeam.teamName}
                      </Typography>
                      <Typography sx={{ fontSize: '14px', color: '#6b7c8f' }}>
                        Portrait and landscape assets stay locked together as a pair on every event.
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {teamDefaultPair ? (
                          <Chip color="primary" label={`Default pair ${teamDefaultPair.identifier}`} />
                        ) : (
                          <Chip label="No team default pair set" />
                        )}
                        <Chip label={`Events ${selectedTeam.events.length}`} variant="outlined" />
                      </Box>
                    </Stack>
                  </Paper>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {selectedTeam.events.map((event) => {
                      const currentPair = getPairByEvent(event);
                      const eventId = getEventServiceId(event);
                      const hasPreviewAssets = Boolean(currentPair);
                      const isSaving = savingEventId === eventId;

                      return (
                        <Card key={eventId || event.name} variant="outlined" sx={{ borderRadius: '18px' }}>
                          <CardContent sx={{ p: 2.5 }}>
                            <Stack spacing={1.5}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                                <Box>
                                  <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#17324d' }}>
                                    {event.name}
                                  </Typography>
                                  <Typography sx={{ fontSize: '13px', color: '#6b7c8f', mt: 0.5 }}>
                                    {event.slogan || 'No event slogan set'}
                                  </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                                  <Chip label={event.statusLabel} size="small" />
                                  {currentPair ? (
                                    <Chip color="primary" size="small" label={`Pair ${currentPair.identifier}`} />
                                  ) : (
                                    <Chip color="warning" size="small" label="No complete pair" />
                                  )}
                                </Box>
                              </Box>

                              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                <Button
                                  variant="contained"
                                  disabled={!hasPreviewAssets}
                                  onClick={() => openPreview(event, 'portrait')}
                                >
                                  Portrait graphics
                                </Button>
                                <Button
                                  variant="outlined"
                                  disabled={!hasPreviewAssets}
                                  onClick={() => openPreview(event, 'landscape')}
                                >
                                  Landscape graphics
                                </Button>
                                <Button
                                  variant="text"
                                  disabled={!currentPair}
                                  onClick={() => currentPair && handleSetTeamDefault(selectedTeam.teamId, currentPair)}
                                >
                                  Set as team default
                                </Button>
                                <Button
                                  variant="text"
                                  disabled={!teamDefaultPair || currentPair?.signature === teamDefaultPair.signature || isSaving}
                                  onClick={() => teamDefaultPair && applyPairToEvent(event, teamDefaultPair)}
                                >
                                  {isSaving ? 'Applying…' : 'Apply team default'}
                                </Button>
                              </Box>
                            </Stack>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </Box>

                  <Paper elevation={0} variant="outlined" sx={{ p: 2.5, borderRadius: '16px' }}>
                    <Stack spacing={2}>
                      <Box>
                        <Typography sx={{ fontSize: '20px', fontWeight: 700, color: '#17324d' }}>
                          Rive pair library
                        </Typography>
                        <Typography sx={{ fontSize: '14px', color: '#6b7c8f', mt: 0.5 }}>
                          Reverse list of paired graphics already used in this organisation. Choose an action from the dropdown to preview or apply a pair without mixing portrait and landscape files.
                        </Typography>
                      </Box>

                      <FormControl fullWidth>
                        <InputLabel id="target-event-label">Target event</InputLabel>
                        <Select
                          labelId="target-event-label"
                          value={selectedTargetEvent ? getEventServiceId(selectedTargetEvent) : ''}
                          label="Target event"
                          onChange={(event) => setTargetEventId(String(event.target.value || ''))}
                        >
                          {(selectedTeam?.events || []).map((event) => (
                            <MenuItem key={getEventServiceId(event)} value={getEventServiceId(event)}>
                              {event.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      {!pairLibrary.length ? (
                        <Alert severity="info">No complete portrait and landscape pairs are available yet.</Alert>
                      ) : (
                        <Stack spacing={1.5}>
                          {pairLibrary.map((pair) => (
                            <Card key={pair.signature} variant="outlined" sx={{ borderRadius: '16px' }}>
                              <CardContent sx={{ p: 2 }}>
                                <Stack spacing={1.5}>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                                    <Box>
                                      <Typography sx={{ fontSize: '17px', fontWeight: 700, color: '#17324d' }}>
                                        {pair.identifier}
                                      </Typography>
                                      <Typography sx={{ fontSize: '13px', color: '#6b7c8f', mt: 0.5 }}>
                                        Used on {pair.usageCount} event{pair.usageCount === 1 ? '' : 's'} • Last updated {formatUpdatedAt(pair.updatedAt)}
                                      </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                      <Chip size="small" label={`${pair.teamNames.length} team${pair.teamNames.length === 1 ? '' : 's'}`} />
                                      <Chip size="small" variant="outlined" label={`Events: ${pair.eventNames.slice(0, 2).join(', ')}${pair.eventNames.length > 2 ? '…' : ''}`} />
                                    </Box>
                                  </Box>

                                  <FormControl fullWidth>
                                    <InputLabel id={`pair-action-${pair.identifier}`}>Action</InputLabel>
                                    <Select
                                      labelId={`pair-action-${pair.identifier}`}
                                      value={pairActions[pair.signature] || ''}
                                      label="Action"
                                      onChange={(event) => handlePairAction(pair, String(event.target.value || '') as TPairAction)}
                                    >
                                      <MenuItem value="">
                                        <em>Select action</em>
                                      </MenuItem>
                                      <MenuItem value="preview-portrait">Preview portrait on target event</MenuItem>
                                      <MenuItem value="preview-landscape">Preview landscape on target event</MenuItem>
                                      <MenuItem value="apply-to-event">Add pair to target event</MenuItem>
                                      <MenuItem value="set-team-default">Set pair as team default</MenuItem>
                                    </Select>
                                  </FormControl>
                                </Stack>
                              </CardContent>
                            </Card>
                          ))}
                        </Stack>
                      )}
                    </Stack>
                  </Paper>
                </>
              ) : (
                <Alert severity="info">Select a team to manage graphics.</Alert>
              )}
            </Box>
          </Box>

          <Dialog open={preview.open} onClose={closePreview} maxWidth="lg" fullWidth>
            <DialogTitle>
              {preview.orientation === 'portrait' ? 'Portrait graphics preview' : 'Landscape graphics preview'}
            </DialogTitle>
            <DialogContent dividers>
              <Stack spacing={2}>
                <Typography sx={{ color: '#5f7285' }}>
                  Preview uses the event title for <strong>_context</strong>, the selected QDOS user name for <strong>_subject</strong>, and the QDOS statement for <strong>_narrative</strong>.
                </Typography>

                {loadingPlayers ? (
                  <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
                    <CircularProgress />
                  </Box>
                ) : null}

                {previewPlayers.length ? (
                  <FormControl fullWidth>
                    <InputLabel id="graphics-player-label">QDOS user</InputLabel>
                    <Select
                      labelId="graphics-player-label"
                      value={selectedPlayer?.ownerId || ''}
                      label="QDOS user"
                      onChange={(event) => setSelectedOwnerId(String(event.target.value || ''))}
                    >
                      {previewPlayers.map((player) => (
                        <MenuItem key={player.ownerId} value={player.ownerId}>
                          {player.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  <Alert severity="info">No QDOS player records were returned for this event preview.</Alert>
                )}

                {previewAsset?.location && preview.event ? (
                  <GraphicsRivePreview
                    src={previewAsset.location}
                    subject={selectedPlayer?.name || 'Name'}
                    context={preview.event.name || 'Event title'}
                    narrative={selectedPlayer?.statement || 'Statement'}
                    orientation={preview.orientation}
                  />
                ) : (
                  <Alert severity="warning">The selected graphics pair is incomplete, so this preview cannot be displayed.</Alert>
                )}
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={closePreview}>Close</Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Box>
  );
};

export default OrganisationGraphicsPanel;
